import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";

import { uniqBy } from "lodash";

import { useFiltersContext } from "./Filters";

import { GamesContextProps, GamesContextValues } from "../types/contexts";
import { SortColumns } from "../types/games";

import { defaultDateTimeFormat } from "../../../../lib/utils";
import { IGame } from "../../../../types/api";
import { SortOrders } from "../../../../types/shared";

const GamesContext = createContext<GamesContextValues | null>(null);

export const useGamesContext = () => {
    const context = useContext(GamesContext);
    if (!context) {
        throw new Error("useGamesContext must be used within a GamesProvider");
    }
    return context;
};

export const GamesProvider: React.FC<GamesContextProps> = props => {
    const { children, initialGames, umpirePay, site } = props;
    const { filters } = useFiltersContext();
    const [games, setGames] = useState<IGame[]>([]);

    useEffect(() => {
        setGames(initialGames);
    }, [initialGames]);

    const [sortColumn, setSortColumn] = useState<SortColumns>("date");
    const [sortOrder, setSortOrder] = useState<SortOrders>("ascending");

    const filteredGames = useMemo(() => {
        return games
            .filter(g =>
                filters.ageLevels.filter
                    ? filters.ageLevels.filter?.includes(g.ageLevel.ageLevel)
                    : true,
            )
            .filter(g => {
                return filters.dateStart
                    ? new Date(
                          defaultDateTimeFormat.format(new Date(g.date)),
                      ) >=
                          new Date(
                              defaultDateTimeFormat.format(filters.dateStart),
                          )
                    : new Date(
                          defaultDateTimeFormat.format(new Date(g.date)),
                      ) >= new Date();
            })
            .filter(g => {
                return filters.dateEnd
                    ? new Date(
                          defaultDateTimeFormat.format(new Date(g.date)),
                      ) <=
                          new Date(
                              defaultDateTimeFormat.format(filters.dateEnd),
                          )
                    : true;
            })
            .filter(g =>
                filters.location.filter
                    ? filters.location.filter?.includes(g.fieldName)
                    : true,
            )
            .filter(g =>
                filters.leagueName.filter
                    ? filters.leagueName.filter?.includes(g.leagueName)
                    : true,
            )
            .filter(g => {
                if (filters.openings) {
                    const pay = umpirePay?.find(
                        u => g.ageLevel.ageLevel === u.ageLevel,
                    );

                    const uniqueUmps = uniqBy(g.umpires, "id");
                    return (
                        g.umpires?.some(
                            u => u.replacement === 1 && !u.pendingReplacement,
                        ) ||
                        (pay?.umpires?.length === 2
                            ? !uniqueUmps.find(u => u.location === "plate") ||
                              !uniqueUmps.find(u => u.location === "field")
                            : !uniqueUmps.find(u => u.location === "plate"))
                    );
                } else {
                    return true;
                }
            });
    }, [games, filters, umpirePay]);

    const sortedGames = useMemo(() => {
        let aValue: any;
        let bValue: any;

        return [...filteredGames].sort((a, b) => {
            switch (sortColumn) {
                case "age":
                    aValue = parseInt(a.ageLevel.ageLevel);
                    bValue = parseInt(b.ageLevel.ageLevel);
                    break;
                case "location":
                    aValue = a.fieldName;
                    bValue = b.fieldName;
                    break;
                case "date":
                    aValue = new Date(a.date).getTime();
                    bValue = new Date(b.date).getTime();
                    break;
            }

            if (sortOrder === "ascending") {
                return aValue > bValue ? 1 : -1;
            } else {
                return aValue < bValue ? 1 : -1;
            }
        });
    }, [filteredGames, sortColumn, sortOrder]);

    const openings = useMemo(() => {
        return filteredGames.reduce((acc, g) => {
            const numUmps = umpirePay.find(
                u => u.ageLevel === g.ageLevel.ageLevel,
            )?.umpires.length;

            const uniqueUmps = uniqBy(g.umpires, "id");
            if (uniqueUmps?.some(u => u.replacement === 1)) {
                return (
                    acc +
                    (numUmps
                        ? numUmps -
                          (uniqueUmps.filter(u => u.replacement !== 1).length ||
                              0)
                        : 0)
                );
            } else {
                return (
                    acc + (numUmps ? numUmps - (uniqueUmps?.length || 0) : 0)
                );
            }
        }, 0);
    }, [filteredGames]);

    const contextValue = useMemo(
        () => ({
            games: sortedGames,
            setGames,
            totalItems: sortedGames.length,
            sortColumn,
            setSortColumn,
            sortOrder,
            setSortOrder,
            umpirePay,
            site,
            openings,
        }),
        [sortedGames],
    );

    return (
        <GamesContext.Provider value={contextValue}>
            {children}
        </GamesContext.Provider>
    );
};
